import styled from 'styled-components/macro'
import { styleReset } from 'styles/misc'

import { mq } from 'styles/mq'
import { IconMagnify } from 'components/icons/magnify'

export const Title = styled.h1`
  text-align: center;
  margin: 0 0 0.2em;
  font-size: clamp(2.5rem, 12vw, 9.6rem);
  font-weight: bold;
  text-transform: uppercase;
`

export const Intro = styled.p`
  width: calc(2 * var(--col-width) + var(--gap));
  max-width: 100%;
  margin: 0 auto 2em;
  text-align: justify;
  text-align-last: center;

  ${mq.ltLarge} {
    hyphens: auto;
  }
`

export const MapContainer = styled.div`
  position: relative;
  border-radius: 3.2rem;
  padding-top: 3.2rem;
  padding-bottom: 3.2rem;

  ${mq.large} {
    margin: 4rem auto 4rem;
    padding-inline: var(--container-spacing);
    max-width: var(--container-width);
  }
`
export const Summary = styled.div`
  width: calc(2 * var(--col-width) + 3 * var(--gap));
  margin: auto auto var(--gap);
  padding: 0 var(--gap);
  max-width: 100%;
  font-size: clamp(2.6rem, 5vw, 4rem);
  text-align: left;
  font-weight: 800;

  > div {
    display: inline-block;
    font-weight: 400;
  }

  ${mq.smallOnly} {
    text-align: center;
  }
`

export const MapSvg = styled.svg`
  display: block;
  width: 100%;
  max-width: 100rem;
  margin: 0 auto;
`

export const Main = styled.main`
  display: grid;
  align-items: start;
  margin-block: 10rem;
  gap: 3rem;

  ${mq.medium} {
    grid-template-columns: clamp(15rem, 25%, 30rem) 1fr;
    gap: 5rem;
  }

  ${mq.large} {
    grid-template-columns: var(--col-width) 1fr;
    gap: var(--gap);
  }
`

export const FiltersIntro = styled.div`
  grid-column: 2 / -1;

  p {
    margin: 0;
  }

  ${mq.smallOnly} {
    text-align: center;
  }

  ${mq.large} {
    font-size: 2.1rem;
  }
`

export const FiltersTitle = styled.h2`
  margin: 1rem 0;
  font-size: clamp(2.6rem, 5vw, 4rem);
  font-weight: bold;
  line-height: 1.2;
`

export const People = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
  gap: var(--gap);
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    font-size: 1.5rem;
    text-align: center;
    line-height: 1.2;
  }

  a {
    text-decoration: none;
  }

  ${mq.smallOnly} {
    grid-template-columns: repeat(auto-fill, minmax(9.6rem, 1fr));
  }

  ${mq.large} {
    grid-template-columns: repeat(6, 1fr);

    li {
      font-size: 1.8rem;
    }
  }
`

export const ProfilePicture = styled.img`
  display: block;
  position: relative;
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  margin-bottom: 0.7em;
  border-radius: 1rem;

  &::before {
    content: '';
    display: block;
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 1rem;
    color: transparent;
    background-color: #ccc;
  }
`

export const Magnify = styled(IconMagnify)`
  position: absolute;
  top: 8px;
  right: 8px;
  filter: drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.5));
  pointer-events: none;
`

export const NoResults = styled.p`
  font-size: 2rem;
  text-align: center;
  align-self: center;

  ${mq.medium} {
    position: sticky;
    bottom: 4rem;
    margin-block: 10rem;
  }
`

export const Disclaimer = styled.div<{ $active: boolean }>`
  position: fixed;
  display: ${props => (props.$active ? 'block' : 'none')};
  inset: 0;
  z-index: 10;

  a {
    word-wrap: break-word;
  }
`

export const DisclaimerInner = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  right: 0;
  margin: auto;
  max-width: 30em;
  font-size: 1.6rem;
  padding: 2rem 3.2rem;
  background: white;
  border-radius: 1.2rem;
`

export const Close = styled.button`
  ${styleReset}
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  right: 0;
  top: 0;
  width: 3.6rem;
  height: 3.6rem;
  font-size: 3rem;
  line-height: 0;
  cursor: pointer;
`

export const DisclaimerBackground = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.2);
`

export const ScrollToTop = styled.button`
  --size: 6rem;

  ${styleReset}
  display: block;
  position: sticky;
  width: var(--size);
  height: var(--size);
  bottom: 0;
  margin: calc(-1 * var(--size)) calc(-1 * var(--container-spacing)) 0 auto;
  box-shadow: 0 -1px 10px #1a394829;
  border-top-left-radius: 2rem;
  background: rgba(255 255 255 / 0.95)
    url("data:image/svg+xml,%3Csvg width='19' height='22' viewBox='0 0 19 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.2522 20.2157C8.2522 20.906 8.81184 21.4657 9.5022 21.4657C10.1926 21.4657 10.7522 20.906 10.7522 20.2157L8.2522 20.2157ZM10.3861 0.478693C9.89792 -0.00946237 9.10647 -0.00946043 8.61831 0.478695L0.663363 8.43365C0.175208 8.9218 0.175208 9.71326 0.663363 10.2014C1.15152 10.6896 1.94298 10.6896 2.43113 10.2014L9.5022 3.13034L16.5733 10.2014C17.0614 10.6896 17.8529 10.6896 18.341 10.2014C18.8292 9.71326 18.8292 8.9218 18.341 8.43364L10.3861 0.478693ZM10.7522 20.2157L10.7522 1.36258L8.2522 1.36258L8.2522 20.2157L10.7522 20.2157Z' fill='%231A3948'/%3E%3C/svg%3E%0A")
    center center no-repeat;

  ${mq.medium} {
    display: none;
  }
`

export const CounterWrapper = styled.div`
  display: inline-block;
  vertical-align: bottom;
  margin: 0 0 -0.4em 0.3rem;
  font-family: 'Jost', sans-serif;
  font-weight: 500;
`
